<template>
  <div id="privacy">
    <h2>Clavo Privacy Policy</h2>
    <p class="subscription">
      Effective as of April 21, 2021.
    </p>
    <p>
      Your privacy is important to us. We provide this notice to help you get a clear understanding of how we collect, use, protect or otherwise treat your personal data that is collected and received through our website <a
        href="https://clavo.app"
        alt="Clavo Website"
      >https://clavo.app</a> (“Website”) and our web-based application Clavo accessible on <a
        href="https://go.clavo.app"
        alt="Clavo Application"
      >https://go.clavo.app</a> (referred to in this document together as “Services” or "Clavo").
    </p>
    <h4>About us</h4>
    <p>Clavo Sp. z o.o. (the “Company”, “we”, or “us”) is a Polish company registered at Pleśniarowicza 5/2, 35-117 Rzeszów, in The Republic of Poland with National Court Register (KRS) number 879920981.</p>
    <h4>The types of personal data we may collect</h4>
    <p>We may collect and receive the following personal data from you:</p>
    <ul>
      <li>Name and contact details (such as name, address, email);</li>
      <li>Account details (such as company, manager details, team member details);</li>
      <li>Information on your use of our Website and Clavo;</li>
      <li>Personal information included in content uploaded by you to Clavo;</li>
      <li>Personal information posted by you on our Website or in Clavo;</li>
      <li>Any other information that you have provided us with (such as your profile picture, the comments you leave, and whether you would like to receive emails about our product).</li>
      <li>Traffic and connection data (such as your computer or mobile device’s IP address, browser type, browser version, type of device and your device’s unique ID, operating system, the pages of our Services that you visit, the time and date of your visit, the time spent on those pages, unique device identifiers and other diagnostic data).</li>
    </ul>
    <h4>How we collect your personal data</h4>
    <p>We collect and receive personal data through our Website and Services. Most of the personal data we collect is directly provided by you. We collect data and process data when you:</p>
    <ul>
      <li>Register online or place an order for any of our products or services.</li>
      <li>Voluntarily complete a customer survey or provide feedback on any of our message boards or via email.</li>
      <li>Use Clavo or view our Website via your browser’s cookies, including similar technologies such as local storage when you visit the website.</li>
    </ul>
    <h4>How we may share your personal data</h4>
    <p>We may share your personal data with third-party service providers in the context of carrying out our services. Those third-party services are restricted to:</p>
    <ul>
      <li>
        Sendinblue <a
          href="https://www.sendinblue.com/"
          alt="Sendinblue"
        >https://www.sendinblue.com/</a> &ndash; for email marketing purposes, e.g. sending newsletters. Sendinblue stores email addresses, name of the subscribers, preferred system, language. You can edit your personal data or unsubscribe from the list by clicking the edit/unsubscribe button on the bottom of a newsletter.
      </li>
      <li>
        Stripe <a
          href="https://stripe.com/"
          alt="Stripe"
        >https://stripe.com/</a> &ndash; for handling the payments and invoices (such as bank credit card information).
      </li>
      <li>
        Sendgrid <a
          href="https://sendgrid.com/"
          alt="Sendgrid"
        >https://sendgrid.com/</a> &ndash; for sending automated emails from our Services.
      </li>
    </ul>
    <p>The third-party services we use are compliant with GDPR regulations. They might have access to your personal data only to carry out tasks listed above on our behalf and are obligated not to disclose or use it for any other purpose.</p>
    <p>We do not sell, rent or trade your personal data.</p>
    <h4>How we store your data</h4>
    <p>We securely store your data using Amazon Web Services (AWS) on servers located in Ireland, EU. However, please note, that we may transfer part of your personal data to third-party service providers located outside the European Union (the "EU") and the European Economic Area (the "EEA"), to carry out our services. </p>
    <h4>How long do we retain personal data</h4>
    <p>We keep your personal data for as long as necessary to fulfill the purposes for which we collect or receive the personal data, except if required otherwise by applicable law.</p>
    <p>Typically, we retain most of the personal data for the duration of your use of our Website and Services, until you have removed your account unless a longer applicable statutory retention period applies. After the deletion of your account, your personal data will be anonymized.</p>
    <h4>How we protect personal data</h4>
    <p>The security of your data is important to us. We maintain appropriate technical security safeguards designed to protect your personal data against accidental, unlawful, or unauthorized destruction, loss, alteration, access, disclosure, or use. However, keep in mind that no method of transmission over the Internet, or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal data, we cannot guarantee it is completely secure.</p>
    <h4>Your data protection rights</h4>
    <p>We would like to make sure that you are fully aware of all of your data protection rights. Every user is entitled to the following:</p>
    <ul>
      <li><strong>The right to access</strong> &ndash; You have the right to request copies of your personal data.</li>
      <li><strong>The right to rectification</strong> &ndash; you have the right to request that we correct any information you believe is inaccurate. You also have the right to request Clavo to complete the information you believe is incomplete.</li>
      <li><strong>The right to erasure</strong> &ndash; You have the right to request that we erase your personal data.</li>
      <li><strong>The right to restrict processing</strong> &ndash; You have the right to request that we restrict the processing of your personal data, under certain conditions.</li>
      <li><strong>The right to object to processing</strong> &ndash; You have the right to object to our processing of your personal data, under certain conditions.</li>
      <li><strong>The right to data portability</strong> &ndash; You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
    </ul>
    <p>
      If you make a request, we have one month to respond to you. If you would like to exercise any of these rights, please feel free to contact us by email: <a
        href="mailto:privacy@clavo.app"
        alt="Contact Us"
      >privacy@clavo.app</a>.
    </p>
    <h4>Cookies</h4>
    <p>Cookies are text files placed on your computer to collect standard Internet log information and visitor behavior information. When you visit our Website and Services, we may collect information from you automatically through cookies or similar technology. Cookies hold information that may be accessible by the party that places the cookie, which is either the website itself (first-party cookie) or a third-party (third-party cookies). You however, do have control over cookies and can refuse the use of cookies by selecting the appropriate setting on your browser. Most browsers will tell you how to stop accepting new cookies, how to be notified when you receive a new cookie, and how to disable or delete existing cookies.</p>
    <p>Please note, that by not accepting or disabling the use of cookies, some of our Website’s and Services’ features may not function as a result.</p>
    <h4>Changes to our privacy policy</h4>
    <p>We may update this Privacy Policy from time to time. We will notify our users of any significant changes to this Privacy Policy on our Website or through other appropriate communication channels. All changes shall be effective from the date of publication unless otherwise provided in the notification.</p>
    <h4>How to contact us</h4>
    <p>
      If you have any questions or require information regarding this Privacy Policy, please feel free to contact us by email: <a
        href="mailto:privacy@clavo.app"
        alt="Contact Us"
      >privacy@clavo.app</a>.
    </p>
  </div>
</template>

<style lang="scss" scoped>
#privacy {
    text-align: left;
    max-width: $maxWidth;
    margin: 166px auto 120px auto;
    padding-right: 120px;
    @include respond-below(md) {
      padding-right: 0;
    }
    h2 {
      margin-bottom: 36px;
    }
    h4 {
      margin-top: 48px;
      margin-bottom: 12px;
    }
    ul {
      @include font-list;
    }
    p {
      @include font-body;
    }
    a {
      @include font-body-link;
    }
    .subscription {
      margin-bottom: 48px;
    }
}
</style>